import React from 'react';
import type { McpServerCandidate } from '../../types';

interface McpTrustSignalsProps {
  candidate: McpServerCandidate;
}

/**
 * McpTrustSignals (AC-1738, epic AC-1732). Surfaces the registry-sourced trust
 * signals for a proposed remote MCP server so the user can judge it at the
 * approval gate. Every signal is rendered AS TEXT (never color-only), mirroring
 * PreviewCard's accessibility convention:
 *
 *   - official-vs-community (registry-asserted `is_official`),
 *   - the publisher (namespace owner),
 *   - the popularity signal (downloads/stars) when present,
 *   - the repository as a link when present.
 *
 * Optional signals degrade gracefully: a missing publisher / popularity renders
 * nothing, and a candidate without a repository_url emits no stray link.
 */
const McpTrustSignals: React.FC<McpTrustSignalsProps> = ({ candidate }) => {
  const {
    is_official,
    publisher,
    popularity,
    repository_url,
  } = candidate;

  return (
    <ul className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
      {/* Trust level: text label, not color-only. A community candidate must
          never carry the word "official". */}
      <li
        className={
          is_official
            ? 'inline-flex items-center rounded px-1.5 py-0.5 font-medium text-green-800 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
            : 'inline-flex items-center rounded px-1.5 py-0.5 font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800'
        }
      >
        {is_official ? 'Official' : 'Community'}
      </li>

      {publisher && (
        <li className="break-all" title={publisher}>
          <span className="text-gray-500 dark:text-gray-500">by </span>
          {publisher}
        </li>
      )}

      {popularity != null && (
        <li title="Registry popularity">
          {popularity.toLocaleString()} uses
        </li>
      )}

      {repository_url && (
        <li>
          <a
            href={repository_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-400 rounded"
          >
            Repository
          </a>
        </li>
      )}
    </ul>
  );
};

export default McpTrustSignals;
